import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { PhoneInput } from '../PhoneInput';
import { requestPushNotificationPermission } from '../../utils/pushNotifications';

interface SMSAuthModalProps {
  onClose: () => void;
}

type AuthStep = 'phone' | 'sms' | 'register';

export function SMSAuthModal({ onClose }: SMSAuthModalProps) {
  const [step, setStep] = useState<AuthStep>('phone');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { sendSMS, verifySMS } = useAuthStore();

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || phone.length !== 11) {
      setError('Введите корректный номер телефона');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Используем НОВУЮ логику authStore - sendSMS сам проверяет сотрудников и клиентов
      const result = await sendSMS(phone);

      console.log('[SMSAuthModal] sendSMS result:', result);

      if (result.success) {
        // Номер найден (сотрудник или клиент) - переходим к вводу SMS
        setStep('sms');
        setTimer(60);
        setCanResend(false);
      } else if (result.userType === 'new') {
        // Номер не найден - переходим к регистрации
        setStep('register');
      } else {
        // Другая ошибка
        setError(result.error || 'Ошибка отправки SMS');
      }
    } catch (err) {
      console.error('[SMSAuthModal] handlePhoneSubmit error:', err);
      setError('Ошибка проверки номера телефона');
    } finally {
      setLoading(false);
    }
  };

  const handleSMSSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsCode || smsCode.length !== 4) {
      setError('Введите 4-значный код из SMS');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Новая логика authStore - verifySMS принимает только код
      const result = await verifySMS(smsCode);

      console.log('[SMSAuthModal] verifySMS result:', result);

      if (result.success) {
        // Закрываем модальное окно
        onClose();

        // Редирект в зависимости от роли
        if (result.userType === 'employee') {
          // Тренер → тренерский интерфейс
          console.log('[SMSAuthModal] Redirecting trainer to /trainer/dashboard');
          navigate('/trainer/dashboard');
        } else {
          // Клиент → клиентский интерфейс (главная страница)
          console.log('[SMSAuthModal] Redirecting client to /');
          navigate('/');
        }

        // Запрашиваем разрешение на push-уведомления после успешной авторизации
        // Это работает потому что клик на "Подтвердить" - это user gesture
        setTimeout(() => {
          // Получаем актуальное значение user из store после авторизации
          const currentUser = useAuthStore.getState().user;
          const clientId = currentUser?.id;
          console.log('[SMSAuthModal] Requesting push permission with clientId:', clientId);
          requestPushNotificationPermission(clientId);
        }, 500);
      } else {
        setError(result.error || 'Неверный код подтверждения');
      }
    } catch (err) {
      console.error('[SMSAuthModal] handleSMSSubmit error:', err);
      setError('Ошибка подтверждения кода');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('Заполните имя и фамилию');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // В новой логике authStore уже отправил SMS при проверке номера
      // Просто переходим к шагу SMS
      setStep('sms');
      setTimer(60);
      setCanResend(false);
    } catch (err) {
      console.error('[SMSAuthModal] handleRegisterSubmit error:', err);
      setError('Ошибка регистрации клиента');
    } finally {
      setLoading(false);
    }
  };

  const handleResendSMS = async () => {
    if (!canResend) return;
    
    setLoading(true);
    setError('');
    
    try {
      const result = await sendSMS(phone);
      if (result.success) {
        setTimer(60);
        setCanResend(false);
        setSmsCode('');
      } else {
        setError(result.error || 'Ошибка отправки SMS');
      }
    } catch (err) {
      setError('Ошибка отправки SMS');
    } finally {
      setLoading(false);
    }
  };

  const formatTimer = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setPhone('');
    setSmsCode('');
    setFirstName('');
    setLastName('');
    setError('');
    setTimer(0);
    setCanResend(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md mx-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {step === 'phone' && 'Вход в аккаунт'}
              {step === 'sms' && 'Подтверждение'}
              {step === 'register' && 'Регистрация'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Phone input step */}
          {step === 'phone' && (
            <form onSubmit={handlePhoneSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Номер телефона
                </label>
                <PhoneInput
                  ref={phoneInputRef}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setError('');
                  }}
                  placeholder="(XXX) XXX-XX-XX"
                  autoFocus
                  error={error && phone.length > 0 && phone.length !== 11 ? 'Введите корректный номер телефона' : undefined}
                />
              </div>
              
              <button
                type="submit"
                disabled={loading || !phone || phone.length !== 11}
                className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Проверяем...' : 'Продолжить'}
              </button>
            </form>
          )}

          {/* Registration step */}
          {step === 'register' && (
            <form onSubmit={handleRegisterSubmit}>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Номер телефона не найден в системе. Для регистрации заполните данные:
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Имя
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setError('');
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      placeholder="Введите имя"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Фамилия
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setError('');
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      placeholder="Введите фамилию"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToPhone}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Назад
                </button>
                <button
                  type="submit"
                  disabled={loading || !firstName.trim() || !lastName.trim()}
                  className="flex-1 bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Регистрируем...' : 'Зарегистрироваться'}
                </button>
              </div>
            </form>
          )}

          {/* SMS verification step */}
          {step === 'sms' && (
            <form onSubmit={handleSMSSubmit}>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Мы отправили 4-значный код подтверждения на номер <strong>+{phone}</strong>
                </p>
                
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Код подтверждения
                </label>
                <input
                  type="text"
                  value={smsCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setSmsCode(value);
                    setError('');
                  }}
                  onKeyDown={(e) => {
                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-center text-2xl font-mono tracking-widest"
                  placeholder="0000"
                  maxLength={4}
                  autoFocus
                  autoComplete="one-time-code"
                />
                
                <div className="flex items-center justify-between mt-3">
                  <button
                    type="button"
                    onClick={handleBackToPhone}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    ← Изменить номер
                  </button>
                  
                  {timer > 0 ? (
                    <span className="text-sm text-gray-500">
                      Повторная отправка через {formatTimer(timer)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendSMS}
                      disabled={loading}
                      className="text-sm text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                    >
                      Отправить повторно
                    </button>
                  )}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading || smsCode.length !== 4}
                className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Проверяем...' : 'Подтвердить'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}